import { useEffect, useState } from 'react';

interface ViewportHeight {
  vh: number;
  realHeight: number;
  isSafari: boolean;
  isMobile: boolean;
}

export const useViewportHeight = (): ViewportHeight => {
  const [viewportHeight, setViewportHeight] = useState<ViewportHeight>({
    vh: window.innerHeight,
    realHeight: window.innerHeight,
    isSafari: false,
    isMobile: false,
  });

  useEffect(() => {
    const detectBrowser = () => {
      const userAgent = navigator.userAgent;
      const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
      const isMobile = /iPhone|iPad|iPod|Android/i.test(userAgent);

      return { isSafari, isMobile };
    };

    const updateViewportHeight = () => {
      const { isSafari, isMobile } = detectBrowser();

      // Default viewport height
      const vh = window.innerHeight;

      // For Safari on iOS, we calculate the real height considering navigation bars
      let realHeight = vh;

      if (isSafari && isMobile) {
        // On Safari iOS, the real height may vary due to navigation bars
        // We use a more conservative approach
        realHeight = Math.min(vh, window.screen.height);

        // If we are in landscape orientation, adjust to account for the navigation bar
        if (window.orientation === 90 || window.orientation === -90) {
          realHeight = Math.min(realHeight, window.screen.width);
        }
      }

      setViewportHeight({
        vh,
        realHeight,
        isSafari,
        isMobile,
      });
    };

    // Update immediately
    updateViewportHeight();

    // Add listeners for orientation and resize changes
    window.addEventListener('resize', updateViewportHeight);
    window.addEventListener('orientationchange', () => {
      // Short delay to ensure the orientation has been applied
      setTimeout(updateViewportHeight, 100);
    });

    // For Safari iOS, also listen for viewport height changes
    if (detectBrowser().isSafari && detectBrowser().isMobile) {
      let lastHeight = window.innerHeight;

      const checkHeightChange = () => {
        const currentHeight = window.innerHeight;
        if (currentHeight !== lastHeight) {
          lastHeight = currentHeight;
          updateViewportHeight();
        }
        requestAnimationFrame(checkHeightChange);
      };

      requestAnimationFrame(checkHeightChange);
    }

    return () => {
      window.removeEventListener('resize', updateViewportHeight);
      window.removeEventListener('orientationchange', updateViewportHeight);
    };
  }, []);

  return viewportHeight;
};
